import React from 'react';
import _ from 'lodash';
import { useAITTheme } from '@this/shared/ui/theme';
import type Trip from '@this/domain/trip/trip2';

interface Props {
  trip: Trip;
  endTime: Date | null;
}

export type StatusCategory = 'inProgress' | 'completed' | 'finished' | 'inactive' | 'error';

export type StatusLabel = {
  text: string;
  category: StatusCategory;
};

export const getStatusLabel = (trip: Trip, endTime: Date | null): StatusLabel | null => {
  if (trip.isApproveCanceled) {
    return { text: '取消済み', category: 'inactive' };
  }

  if (trip.currentApprovalStatus === 2) {
    return { text: '却下済み', category: 'error' };
  }

  if (trip.status === 4 || trip.status === 7) {
    if (endTime && endTime.valueOf() < Date.now()) {
      return { text: '出張完了', category: 'finished' };
    }
    return { text: '手配完了', category: 'completed' };
  }

  if (trip.currentApprovalStatus === 0) {
    return { text: '承認待ち', category: 'inactive' };
  }

  if (trip.status === 3) {
    const activeOrderItems = trip.currentOrder?.orderItems?.filter(item => !item.deletedAt) || [];
    if (activeOrderItems.length > 0 && activeOrderItems.every(item => item.status === 13 || item.status === 7)) {
      return { text: '手配完了', category: 'completed' };
    }
  }

  if (!(!trip.currentOrder && trip.nonOrderItems) && trip.isAllOrderItemStatusComplete) {
    return { text: '手配完了', category: 'inProgress' };
  }

  if (trip.status === 0) {
    return { text: '旅程リクエスト中', category: 'inProgress' };
  }

  if (trip.status === 6) {
    return { text: 'キャンセル依頼中', category: 'inactive' };
  }

  if (trip.status === 5) {
    return { text: 'キャンセル済み', category: 'inactive' };
  }

  if (trip.status === 8) {
    return { text: 'ワークフロー申請待ち', category: 'inProgress' };
  }

  if (trip.status === 9) {
    return { text: 'ワークフロー承認待ち', category: 'inProgress' };
  }

  if (trip.status === 10) {
    return { text: 'ワークフロー再申請待ち', category: 'inProgress' };
  }

  if (trip.status === 11) {
    return { text: 'ワークフロー却下', category: 'error' };
  }

  if (trip.status === 12) {
    return { text: 'ワークフロー削除済み', category: 'inactive' };
  }

  if (trip.status === 3) {
    if (!trip.currentOrder && trip.nonOrderItems) {
      if (endTime && endTime.valueOf() < Date.now()) {
        return { text: '出張完了', category: 'finished' };
      }

      if (trip.inAdvanceApprovals && trip.inAdvanceApprovals.length > 0) {
        if (trip.currentApprovalStatus === 1) {
          return { text: '承認済み', category: 'inactive' };
        }
        return { text: '旅程作成済', category: 'completed' };
      }

      return { text: '旅程作成済', category: 'completed' };
    }

    return { text: '手配中', category: 'inProgress' };
  }

  return { text: '手配中', category: 'inProgress' };
};

const StatusLabelTemplate: React.FC<Props> = ({ trip, endTime }) => {
  const { theme } = useAITTheme();
  const { colors } = theme.tokens;
  const label = getStatusLabel(trip, endTime);

  if (!label) {
    return null;
  }

  const colorMap: Record<StatusCategory, string> = {
    inProgress: colors.brand.primary,
    completed: colors.success.primary,
    finished: '#4A4A94',
    inactive: '#666666',
    error: colors.danger.primary
  };

  const color = colorMap[label.category];

  return <p style={{ color, margin: 0 }}>{label.text}</p>;
};

export default StatusLabelTemplate;
